import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  doc, 
  setDoc, 
  getDoc 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export function CartProvider({ children }) {
  const [cart, setCart] = useState([]);
  const { user } = useAuth();

  // Toplam ürün sayısını hesapla
  const cartItemCount = cart.reduce((total, item) => total + (item.quantity || 1), 0);

  // Sepet toplamını hesapla
  const totalAmount = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;

  // Sepeti yükle
  useEffect(() => {
    const loadCart = async () => {
      if (user) {
        try {
          const cartRef = doc(db, 'carts', user.uid);
          const cartDoc = await getDoc(cartRef);
          if (cartDoc.exists()) {
            setCart(cartDoc.data().items || []);
          }
        } catch (error) {
          console.error('Sepet yüklenirken hata:', error);
        }
      } else {
        // Kullanıcı giriş yapmamışsa local storage'dan yükle
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
          setCart(JSON.parse(savedCart));
        }
      }
    };
    loadCart();
  }, [user]);

  // Sepeti kaydet
  useEffect(() => {
    if (user) {
      const saveCart = async () => {
        try {
          const cartRef = doc(db, 'carts', user.uid);
          await setDoc(cartRef, { items: cart }, { merge: true });
        } catch (error) {
          console.error('Sepet kaydedilirken hata:', error);
        }
      };
      if (cart.length > 0) {
        saveCart();
      }
    } else {
      // Kullanıcı giriş yapmamışsa local storage'a kaydet
      localStorage.setItem('cart', JSON.stringify(cart));
    }
  }, [cart, user]);

  const addToCart = (product, quantity = 1, size, color) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(
        item => item.id === product.id && item.size === size && item.color === color
      );

      if (existingItem) {
        return prevCart.map(item =>
          item.id === product.id && item.size === size && item.color === color
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [...prevCart, { ...product, quantity, size, color }];
    });
  };

  const removeFromCart = (productId, size, color) => {
    setCart(prevCart => prevCart.filter(
      item => !(item.id === productId && item.size === size && item.color === color)
    ));
  };

  const updateQuantity = (productId, size, color, newQuantity) => {
    if (newQuantity < 1) return;
    
    setCart(prevCart => prevCart.map(item =>
      item.id === productId && item.size === size && item.color === color
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const clearCart = () => {
    setCart([]);
  };

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      totalAmount,
      cartItemCount
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
} 